import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardPageHeader from "../../components/Dashboard/DashboardPageHeader";
import { getShowingText, translateGenericStatus } from "../../i18n/dashboardHelpers";
import { getAppointments, updateAppointmentStatus } from "../../services/apiService";

const PAGE_SIZE = 20;

const APPOINTMENT_STATUS = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  CANCELLED: "cancelled",
  COMPLETED: "completed",
  NO_SHOW: "no_show",
};

const tabs = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending", status: "pending" },
  { id: "confirmed", label: "Confirmed", status: "confirmed" },
  { id: "cancelled", label: "Cancelled", status: "cancelled" },
  { id: "completed", label: "Completed", status: "completed" },
  { id: "no_show", label: "No Show", status: "no_show" },
];

const statusStyles = {
  PENDING: "dashboard-badge--pending",
  CONFIRMED: "dashboard-badge--active",
  CANCELLED: "dashboard-badge--canceled",
  COMPLETED: "dashboard-badge--completed",
  NO_SHOW: "dashboard-badge--inactive",
};

function extractItems(response) {
  if (Array.isArray(response?.data)) return response.data;
  return response?.data?.data ?? [];
}

function extractTotal(response, fallback = 0) {
  return (
    response?.pagination?.totalItems ??
    response?.data?.total ??
    response?.data?.meta?.total ??
    fallback
  );
}

function extractTotalPages(response, totalItems = 0) {
  if (response?.pagination?.totalPages != null) {
    return response.pagination.totalPages;
  }
  return totalItems === 0 ? 0 : Math.ceil(totalItems / PAGE_SIZE);
}

function getPaginationPages(currentPage, totalPages) {
  if (totalPages <= 0) return [];

  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, "ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
}

function formatDate(value, locale) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

function avatarColor(name = "") {
  const colors = ["#DBEAFE", "#D1FAE5", "#FEF3C7", "#FCE7F3", "#E5E7EB"];
  const index = name.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) % colors.length;
  return colors[index];
}

function formatPhone(countryCode, phoneNumber) {
  if (!phoneNumber) return "—";
  return `${countryCode || ""}${phoneNumber}`.trim() || "—";
}

function getAppointmentName(appointment) {
  return appointment.full_name || appointment.fullName || "—";
}

function getAppointmentPhone(appointment) {
  return formatPhone(
    appointment.country_code || appointment.countryCode,
    appointment.phone_number || appointment.phoneNumber
  );
}


function StatusBadge({ status, t }) {
  const label = translateGenericStatus(t, status);
  const styleKey = String(status || "").toUpperCase();
  return <span className={`dashboard-badge ${statusStyles[styleKey] || ""}`}>{label}</span>;
}

function CompleteIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CancelIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function isActionableStatus(status) {
  const normalized = status?.toLowerCase();
  return (
    normalized === APPOINTMENT_STATUS.PENDING || normalized === APPOINTMENT_STATUS.CONFIRMED
  );
}

function AppointmentStatusConfirmModal({ confirmAction, onClose, onConfirm, t }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!confirmAction) {
      setIsSubmitting(false);
      setError("");
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSubmitting) onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [confirmAction, isSubmitting, onClose]);

  if (!confirmAction) return null;

  const { appointment, action } = confirmAction;
  const name = getAppointmentName(appointment);
  const isComplete = action === "complete";
  const status = isComplete ? APPOINTMENT_STATUS.COMPLETED : APPOINTMENT_STATUS.CANCELLED;
  const title = isComplete
    ? t("dashboard.appointments.completeTitle")
    : t("dashboard.appointments.cancelTitle");
  const message = isComplete
    ? t("dashboard.appointments.completeMessage", { name })
    : t("dashboard.appointments.cancelMessage", { name });

  const handleConfirm = async () => {
    setError("");
    setIsSubmitting(true);

    try {
      await updateAppointmentStatus(appointment.id, status);
      onConfirm?.();
      onClose();
    } catch (fetchError) {
      setError(
        fetchError?.response?.data?.message ||
          fetchError?.message ||
          t("dashboard.appointments.updateFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="settings-modal-overlay"
      onClick={isSubmitting ? undefined : onClose}
      role="presentation"
    >
      <div
        className="settings-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="appointment-status-confirm-title"
      >
        <div className="settings-modal__header">
          <h2 id="appointment-status-confirm-title" className="settings-modal__title">
            {title}
          </h2>
          <button
            type="button"
            className="settings-modal__close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label={t("dashboard.common.close")}
          >
            &times;
          </button>
        </div>

        <p className="settings-modal__subtitle">{message}</p>

        {error ? (
          <p className="settings-form-status settings-form-status--error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="settings-modal__actions">
          <button
            type="button"
            className="settings-btn settings-btn--outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("dashboard.common.cancel")}
          </button>
          <button
            type="button"
            className={`settings-btn ${isComplete ? "settings-btn--primary" : "settings-btn--danger"}`}
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t("dashboard.common.updating")
              : isComplete
                ? t("dashboard.common.complete")
                : t("dashboard.appointments.cancelTitle")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Appointments() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [appointments, setAppointments] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const activeStatus = tabs.find((tab) => tab.id === activeTab)?.status;

  useEffect(() => {
    let cancelled = false;

    const loadAppointments = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getAppointments({
          status: activeStatus,
          page,
          pageSize: PAGE_SIZE,
        });

        if (cancelled) return;

        const items = extractItems(response);
        const total = extractTotal(response, 0);
        setAppointments(items);
        setTotalItems(total);
        setTotalPages(extractTotalPages(response, total));
      } catch (fetchError) {
        if (cancelled) return;

        setAppointments([]);
        setTotalItems(0);
        setTotalPages(0);
        setError(fetchError?.response?.data?.message || t("dashboard.appointments.loadFailed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAppointments();

    return () => {
      cancelled = true;
    };
  }, [activeStatus, page, refreshKey, t]);

  const handleStatusUpdated = () => {
    setRefreshKey((current) => current + 1);
  };

  const openStatusConfirm = (appointment, action) => {
    setStatusConfirm({ appointment, action });
  };

  const filteredAppointments = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return appointments;

    return appointments.filter((appointment) => {
      const name = getAppointmentName(appointment).toLowerCase();
      const email = appointment.email?.toLowerCase() ?? "";
      const message = appointment.message?.toLowerCase() ?? "";
      const phone = getAppointmentPhone(appointment).toLowerCase();

      return (
        name.includes(query) ||
        email.includes(query) ||
        message.includes(query) ||
        phone.includes(query)
      );
    });
  }, [appointments, search]);

  const paginationPages = getPaginationPages(page, totalPages);
  const rangeStart = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalItems);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPage(1);
  };

  return (
    <div className="dashboard-page">
      <DashboardPageHeader title={t("dashboard.appointments.title")}>
        <label className="dashboard-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            placeholder={t("dashboard.appointments.searchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label={t("dashboard.appointments.searchAria")}
          />
        </label>
      </DashboardPageHeader>

      <div className="dashboard-card dashboard-card--table">
        <div className="dashboard-table-toolbar">
          <h2 className="dashboard-card__title dashboard-card__title--inline">{t("dashboard.appointments.title")}</h2>
          <div className="dashboard-tabs" role="tablist" aria-label={t("dashboard.appointments.filterAria")}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`dashboard-tabs__item${activeTab === tab.id ? " dashboard-tabs__item--active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.id === "all" ? t("dashboard.common.all") : translateGenericStatus(t, tab.status)}
              </button>
            ))}
          </div>
        </div>

        <div className="dashboard-table-wrap">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>{t("dashboard.common.customer")}</th>
                <th>{t("dashboard.common.email")}</th>
                <th>{t("dashboard.common.phone")}</th>
                <th>{t("dashboard.appointments.scheduled")}</th>
                <th>{t("common.message")}</th>
                <th>{t("dashboard.common.status")}</th>
                <th>{t("dashboard.common.submitted")}</th>
                <th>{t("dashboard.common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8}>{t("dashboard.appointments.loading")}</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8}>{error}</td>
                </tr>
              ) : filteredAppointments.length === 0 ? (
                <tr>
                  <td colSpan={8}>{t("dashboard.appointments.noAppointments")}</td>
                </tr>
              ) : (
                filteredAppointments.map((appointment) => {
                  const name = getAppointmentName(appointment);
                  return (
                    <tr key={appointment.id}>
                      <td>
                        <div className="dashboard-table__customer">
                          <span
                            className="dashboard-table__avatar"
                            style={{ backgroundColor: avatarColor(name) }}
                            aria-hidden="true"
                          >
                            {getInitials(name)}
                          </span>
                          <span className="dashboard-table__name">{name}</span>
                        </div>
                      </td>
                      <td className="dashboard-table__email">{appointment.email || "—"}</td>
                      <td>{getAppointmentPhone(appointment)}</td>
                      <td className="dashboard-table__date">{formatDate(appointment.date, i18n.language)}</td>
                      <td>{appointment.message || "—"}</td>
                      <td>
                        <StatusBadge status={appointment.status} t={t} />
                      </td>
                      <td className="dashboard-table__date">
                        {formatDate(appointment.created_at || appointment.createdAt, i18n.language)}
                      </td>
                      <td>
                        {isActionableStatus(appointment.status) ? (
                          <div className="dashboard-table__actions">
                            <button
                              type="button"
                              className="dashboard-table__actions-btn dashboard-table__actions-btn--approve"
                              aria-label={t("dashboard.appointments.completeMessage", { name })}
                              onClick={() => openStatusConfirm(appointment, "complete")}
                            >
                              <CompleteIcon />
                            </button>
                            <button
                              type="button"
                              className="dashboard-table__actions-btn dashboard-table__actions-btn--reject"
                              aria-label={t("dashboard.appointments.cancelMessage", { name })}
                              onClick={() => openStatusConfirm(appointment, "cancel")}
                            >
                              <CancelIcon />
                            </button>
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="dashboard-table-footer">
          <p className="dashboard-table-footer__info">
            {getShowingText(t, {
              entity: "appointments",
              start: rangeStart,
              end: rangeEnd,
              total: totalItems,
            })}
          </p>
          <nav className="dashboard-pagination" aria-label={t("dashboard.appointments.paginationAria")}>
            <button
              type="button"
              className="dashboard-pagination__btn"
              aria-label={t("dashboard.common.previousPage")}
              disabled={page <= 1 || loading || totalPages === 0}
              onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {paginationPages.map((pageNumber, index) =>
              pageNumber === "ellipsis" ? (
                <span key={`ellipsis-${index}`} className="dashboard-pagination__ellipsis" aria-hidden="true">
                  …
                </span>
              ) : (
                <button
                  key={pageNumber}
                  type="button"
                  className={`dashboard-pagination__page${
                    page === pageNumber ? " dashboard-pagination__page--active" : ""
                  }`}
                  aria-current={page === pageNumber ? "page" : undefined}
                  disabled={loading}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              )
            )}
            <button
              type="button"
              className="dashboard-pagination__btn"
              aria-label={t("dashboard.common.nextPage")}
              disabled={page >= totalPages || loading || totalPages === 0}
              onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9 18l6-6-6-6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </nav>
        </div>
      </div>

      <AppointmentStatusConfirmModal
        confirmAction={statusConfirm}
        onClose={() => setStatusConfirm(null)}
        onConfirm={handleStatusUpdated}
        t={t}
      />
    </div>
  );
}

export default Appointments;
